import React, { useState } from "react";
import { G, useStore, useIsGM } from "../store";
import { setGame, toast } from "../net";
import { api } from "../api";
import { Field, confirm } from "../util";

export function SettingsPanel() {
  const gm = useIsGM();
  const game = useStore(G, s => s.game)!;
  const members = useStore(G, s => s.members);
  const user = useStore(G, s => s.user);
  const me = useStore(G, s => s.me);
  const invite = game.joinCode ? `${location.origin}/#/join/${game.joinCode}` : "";
  const [pw, setPw] = useState({ old: "", neu: "" });

  const refreshMe = async (body: any) => {
    try {
      const r = await api("/api/me", { method: "PATCH", body });
      G.set({ user: r.user });
      toast("Saved");
    } catch (e: any) { toast(e.message); }
  };
  const s = game.settings;
  const setS = (patch: any) => setGame({ settings: patch });

  return (
    <div className="settings pad">
      {gm && (
        <section>
          <div className="box-title">Game</div>
          <label>Name <Field value={game.name} onCommit={v => setGame({ name: v })} /></label>
          <div className="box-title">Invite players</div>
          <div className="row">
            <input readOnly value={invite} onFocus={e => e.target.select()} />
            <button onClick={() => { navigator.clipboard?.writeText(invite); toast("Invite link copied"); }}>Copy</button>
          </div>
          <div className="dim small">Anyone with this link can create an account and join as a player.
            <button className="link" onClick={async () => { if (await confirm("Reset the invite link? The old link stops working.")) await api(`/api/games/${game.id}/invite`, { method: "POST" }); }}>Reset link</button>
          </div>
          <div className="box-title">Players</div>
          {members.map(m => (
            <div key={m.uid} className="row member">
              <span className="msg-dot" style={{ background: m.color }} />
              <span className="grow">{m.display}{m.uid === game.owner ? " (owner)" : ""}</span>
              {m.uid !== game.owner && (
                <>
                  <select value={m.role} onChange={async e => { await api(`/api/games/${game.id}/members/${m.uid}`, { method: "PATCH", body: { role: e.target.value } }); }}>
                    <option value="player">Player</option>
                    <option value="gm">GM</option>
                  </select>
                  <button className="mini danger" title="Remove from game" onClick={async () => { if (await confirm(`Remove ${m.display} from the game?`)) await api(`/api/games/${game.id}/members/${m.uid}`, { method: "DELETE" }); }}>✕</button>
                </>
              )}
            </div>
          ))}
          <div className="box-title">Rules</div>
          <label>Character sheet
            <select value={s.sheet} onChange={e => setS({ sheet: e.target.value })}>
              <option value="dnd5e">D&D 5th Edition</option>
              <option value="none">None (attributes & abilities only)</option>
            </select>
          </label>
          <label className="chk"><input type="checkbox" checked={s.playersCanDraw} onChange={e => setS({ playersCanDraw: e.target.checked })} /> Players can draw & write on the map</label>
          <label className="chk"><input type="checkbox" checked={s.playersCreateChars} onChange={e => setS({ playersCreateChars: e.target.checked })} /> Players can create characters</label>
          <label className="chk"><input type="checkbox" checked={s.playersOpenDoors} onChange={e => setS({ playersOpenDoors: e.target.checked })} /> Players can open & close doors</label>
          <div className="box-title">Backup</div>
          <div className="row gap wrap">
            <a className="btn" href={`/api/games/${game.id}/export`} download>⬇ Export game (.json)</a>
            <button onClick={async () => { const r = await api(`/api/games/${game.id}/copy`, { method: "POST" }); toast("Copied — find it in your games list"); void r; }}>Duplicate game</button>
          </div>
          {me === game.owner && (
            <button className="danger" onClick={async () => {
              if (!(await confirm(`Delete "${game.name}" forever? Everything in it is lost.`))) return;
              await api(`/api/games/${game.id}`, { method: "DELETE" });
              location.hash = "#/";
            }}>Delete game</button>
          )}
        </section>
      )}
      <section>
        <div className="box-title">You</div>
        <label>Display name <Field value={user?.display || ""} onCommit={v => refreshMe({ display: v })} /></label>
        <label>Color <input type="color" value={user?.color || "#888888"} onChange={e => refreshMe({ color: e.target.value })} /></label>
        <form className="col" onSubmit={e => { e.preventDefault(); refreshMe({ oldPassword: pw.old, newPassword: pw.neu }).then(() => setPw({ old: "", neu: "" })); }}>
          <input type="password" placeholder="Current password" value={pw.old} onChange={e => setPw({ ...pw, old: e.target.value })} autoComplete="current-password" />
          <input type="password" placeholder="New password (8+)" value={pw.neu} onChange={e => setPw({ ...pw, neu: e.target.value })} autoComplete="new-password" />
          <button type="submit" disabled={!pw.old || pw.neu.length < 8}>Change password</button>
        </form>
        {!gm && (
          <button className="danger" onClick={async () => {
            if (!(await confirm("Leave this game?"))) return;
            await api(`/api/games/${game.id}/leave`, { method: "POST" });
            location.hash = "#/";
          }}>Leave game</button>
        )}
      </section>
      <Help />
    </div>
  );
}

function Help() {
  return (
    <section className="help">
      <div className="box-title">Quick reference</div>
      <details open>
        <summary>Map controls</summary>
        <ul>
          <li><b>Wheel</b> zoom · <b>right/middle-drag</b> or <b>Space+drag</b> pan · trackpad two-finger pan, pinch zoom</li>
          <li><b>Hold click</b> ~½s to ping · GM: <b>Shift+hold</b> pulls everyone's view</li>
          <li><b>Shift+click</b> multi-select · drag empty space to box-select · <b>Alt</b> while dropping skips grid snap</li>
          <li><b>Double-click</b> a token for its settings · right-click for the menu</li>
          <li><b>Del</b> delete · <b>Ctrl+C/V</b> copy/paste · <b>Ctrl+Z</b> undo · <b>arrows</b> nudge by a square (Shift = 1px)</li>
          <li>Tools: <b>V</b> select · <b>H</b> pan · <b>R</b> ruler · <b>D</b> draw · <b>T</b> text · polygons/walls: click points, <b>double-click</b> or <b>Enter</b> to finish, <b>Esc</b> cancels</li>
        </ul>
      </details>
      <details>
        <summary>Chat & dice</summary>
        <ul>
          <li><code>/r 2d6+3 fire damage</code> · <code>/gr</code> GM-only roll · <code>/w name msg</code> · <code>/w gm msg</code></li>
          <li><code>/em</code> emote · <code>/ooc</code> · GM: <code>/desc</code>, <code>/as "Name" text</code>, <code>/clearchat</code></li>
          <li>Inline rolls: <code>I hit for [[1d8+3]]!</code></li>
          <li><code>4d6kh3</code> keep highest · <code>2d20kl1</code> keep lowest · <code>4d6dl1</code> drop lowest</li>
          <li><code>3d6!</code> exploding · <code>3d6!!</code> compounding · <code>2d6r1</code> reroll 1s · <code>2d6ro&lt;2</code> reroll once</li>
          <li><code>6d10&gt;7</code> count successes · <code>f1</code> subtract failures · <code>4dF</code> fate · <code>d%</code></li>
          <li><code>{"{"}1d20+5, 15{"}"}kh1</code> groups · <code>1t[Loot]</code> tables · <code>1d6[fire]</code> labels · <code>floor() ceil() round() abs()</code></li>
          <li><code>@{"{"}Char|attr{"}"}</code> · <code>@{"{"}selected|bar1{"}"}</code> · <code>%{"{"}Char|ability{"}"}</code> · <code>#macro</code> · <code>?{"{"}Bonus|0{"}"}</code></li>
          <li><code>&amp;{"{"}template:default{"}"} {"{{"}name=Title{"}}"} {"{{"}Hit=[[1d20]]{"}}"}</code> · add <code>&amp;{"{"}tracker{"}"}</code> in a roll to put the result in the turn order</li>
        </ul>
      </details>
    </section>
  );
}
